import type { Connection, ConnectionMap } from '../../../types/connection';
import type { NodeConnectionTag } from '../../../types/node';
import type { Relation, RelationMap } from '../../../types/relation';
import {
  getChildEndX,
  getChildEndY,
  getChildOffset,
  getChildStartX,
  getChildStartY,
} from '../../canvas/calculateChildPosition/calculateChildPosition';

export const createConnection = ({
  relation,
  parent,
  tag,
  position,
}: {
  relation: Relation;
  parent: Relation;
  tag?: NodeConnectionTag;
  position: {
    offset: number;
    startX: number;
    startY: number;
  };
}): Connection => {
  const { offset, startX, startY } = position;
  const {
    id,
    connectionLabel,
    height,
    pageX: preCalculatedEndX,
    pageY: preCalculatedEndY,
  } = relation;

  const label = connectionLabel !== 'arrow' ? connectionLabel : null;
  const endX = getChildEndX(preCalculatedEndX);
  const endY = getChildEndY(preCalculatedEndY);

  return {
    id,
    parentId: parent.id,
    label,
    tag,
    offset: {
      x: offset,
      y: parent.height,
    },
    target: {
      height,
    },
    startX,
    startY,
    endX,
    endY,
  };
};

export const createVisualConnection = ({
  id,
  label,
  tag,
  parent,
  position,
}: {
  id: string;
  label: string | null;
  tag: NodeConnectionTag;
  parent: Relation;
  position: {
    offset: number;
    startX: number;
    startY: number;
  };
}): Connection => ({
  id,
  parentId: parent.id,
  label,
  tag,
  offset: {
    x: position.offset,
    y: parent.height,
  },
  target: {
    height: 0,
  },
  startX: position.startX,
  startY: position.startY,
  endX: position.startX + position.offset,
  endY: position.startY + 200,
});

export const getConnections = (relations: RelationMap): ConnectionMap =>
  Object.values(relations).reduce<ConnectionMap>((connections, node) => {
    const {
      pageX: preCalculatedStartX,
      pageY: preCalculatedStartY,
      connections: dependencies,
    } = node;
    const amountOfOuts = dependencies.length || 1;

    dependencies.forEach((nodeConnection, index) => {
      const { connection } = nodeConnection;

      const position = index + 1;

      const offset = getChildOffset(amountOfOuts, position);
      const startX = getChildStartX(preCalculatedStartX);
      const startY = getChildStartY(preCalculatedStartY);

      if (connection != null) {
        const { connection, tag } = nodeConnection;
        const relation = relations[connection];

        if (!relation) {
          throw new Error(
            `Missing relation "${connection}" required for a connection.`,
          );
        }

        connections[connection] = createConnection({
          relation,
          parent: node,
          tag,
          position: {
            offset,
            startX,
            startY,
          },
        });

        return;
      }

      const { id, label, tag } = nodeConnection;

      connections[id] = createVisualConnection({
        id,
        label,
        tag,
        parent: node,
        position: {
          offset,
          startX,
          startY,
        },
      });

      return;
    });

    return connections;
  }, {});
